import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, sanitizeEmbed } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors";

export default function CaseStudyTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/case-study")
      .then(({ data }) => setForm({ title: data.title || "", description: data.description || "", embed_code: data.embed_code || "" }))
      .catch(() => setForm({ title: "", description: "", embed_code: "" }));
  }, []);
  const save = async () => {
    if (!form.title || !form.description) { toast.error("Title and description are required"); return; }
    setSaving(true);
    try { await api.put("/admin/content/case-study", form); toast.success("Case study updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!form) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#FF4D00]" /></div>;
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="case-study-form">
      <div className="border border-[#2A2A2A] bg-[#111111] p-6 space-y-4">
        <h3 className="font-bebas text-2xl tracking-wide">FEATURED CASE STUDY · THANK YOU PAGE</h3>
        <p className="text-sm text-[#A1A1AA]">This content powers the Featured Case Study block shown after a client submits the inquiry form.</p>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Case study title</label>
          <input data-testid="cs-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Description paragraph</label>
          <textarea data-testid="cs-description-input" className={`${inputCls} min-h-[120px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Bunny.net embed code</label>
          <textarea data-testid="cs-embed-input" className={`${inputCls} min-h-[160px] font-mono text-xs`} placeholder="Paste the full <iframe …></iframe> embed code from Bunny.net Stream" value={form.embed_code} onChange={(e) => setForm({ ...form, embed_code: e.target.value })} />
          <p className="text-xs text-[#555] mt-1">Paste the full iframe embed code. Leave blank to show the default placeholder.</p>
        </div>
        <button data-testid="cs-save-btn" onClick={save} disabled={saving} className="bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save case study</button>
      </div>

      <div className="border border-[#2A2A2A] bg-[#111111] p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#FF4D00] mb-3">Live Video Preview</div>
        {form.embed_code ? (
          <div className="w-full bg-black embed-fill overflow-hidden" data-testid="cs-preview" dangerouslySetInnerHTML={{ __html: sanitizeEmbed(form.embed_code) }} />
        ) : (
          <div className="w-full aspect-video bg-[#050505] border border-dashed border-[#2A2A2A] flex items-center justify-center text-sm text-[#555]" data-testid="cs-preview-empty">Paste an embed code to preview the player</div>
        )}
        <p className="text-xs text-[#555] mt-3">This preview renders the embed instantly — it does not save until you click “Save case study”.</p>
      </div>
    </div>
  );
}
